import { normalizeSearchGender } from "./patientSearch";
import { EMPTY_PATIENT } from "../constants/registrationConstants";

function parseDocumentDob(data) {
  const yob = data.yearOfBirth;
  const mob = data.monthOfBirth ? String(data.monthOfBirth).padStart(2, "0") : "";
  const dayob = data.dayOfBirth ? String(data.dayOfBirth).padStart(2, "0") : "";

  if (yob && mob && dayob) return `${yob}-${mob}-${dayob}`;
  if (data.dateOfBirth && /^\d{4}-\d{2}-\d{2}$/.test(data.dateOfBirth)) return data.dateOfBirth;
  if (data.dateOfBirth && /^\d{2}-\d{2}-\d{4}$/.test(data.dateOfBirth)) {
    const [d, m, y] = data.dateOfBirth.split("-");
    return `${y}-${m}-${d}`;
  }
  if (data.dob && /^\d{4}-\d{2}-\d{2}$/.test(data.dob)) return data.dob;
  return "";
}

function emptyAddress() {
  return {
    type: "PERMANENT",
    line1: "",
    city: "",
    district: "",
    state: "",
    country: "India",
    pincode: "",
  };
}

function emptyEmergencyContact() {
  return { name: "", relation: "", phone: "", address: "" };
}

/**
 * Patches the admission patient form from document_json — same fields as
 * RegisterPatientForIpd.js autofillFromDocumentJson (only sets present values).
 */
export function applyDocumentAutofillToPatient(patient, data) {
  if (!data || typeof data !== "object") return patient;

  const next = { ...patient };

  if (data.name || data.patientName) {
    next.name = data.name || data.patientName;
  } else if (data.firstname || data.lastname) {
    const composed = `${data.firstname || ""} ${data.lastname || ""}`.trim();
    if (composed) next.name = composed;
  }

  if (data.gender) {
    const g = data.gender === "Male" ? "M" : data.gender === "Female" ? "F" : data.gender;
    next.gender = normalizeSearchGender(g);
  }

  const dob = parseDocumentDob(data);
  if (dob) next.dob = dob;

  if (data.marital_status) next.maritalStatus = String(data.marital_status).toUpperCase();
  if (data.occupation) next.occupation = data.occupation;
  if (data.nationality) next.nationality = data.nationality;
  if (data.religion) next.religion = data.religion;

  if (data.phoneNumber) {
    next.primaryMobile = data.phoneNumber;
    const contacts = [...(next.contacts || [])];
    const phoneIdx = contacts.findIndex((c) => c.channel === "PHONE" && c.type === "PRIMARY");
    if (phoneIdx >= 0) {
      contacts[phoneIdx] = { ...contacts[phoneIdx], value: data.phoneNumber };
    } else {
      contacts.unshift({ channel: "PHONE", type: "PRIMARY", value: data.phoneNumber });
    }
    next.contacts = contacts;
  }

  const email = data.email || data.emailId;
  if (email) {
    const contacts = [...(next.contacts || [])];
    const emailIdx = contacts.findIndex((c) => c.channel === "EMAIL");
    if (emailIdx >= 0) {
      contacts[emailIdx] = { ...contacts[emailIdx], value: email };
    } else {
      contacts.push({ channel: "EMAIL", type: "PRIMARY", value: email });
    }
    next.contacts = contacts;
  }

  if (data.locality || data.pincode || data.state || data.city || data.district || data.country) {
    const addresses = [...(next.addresses?.length ? next.addresses : [emptyAddress()])];
    const addr = { ...addresses[0] };
    if (data.locality) addr.line1 = data.locality;
    if (data.city) addr.city = data.city;
    if (data.district) addr.district = String(data.district).toLowerCase();
    if (data.state) addr.state = String(data.state).toLowerCase();
    if (data.country) addr.country = data.country;
    if (data.pincode) addr.pincode = data.pincode;
    addresses[0] = addr;
    next.addresses = addresses;
  }

  const ecName = data.emergency_contact_name;
  const ecPhone = data.emergency_contact_phone;
  const ecRelation = data.emergency_contact_relation;
  const ecAddress = data.emergency_contact_address;
  if (ecName || ecPhone || ecRelation || ecAddress) {
    const emergencyContacts = [...(next.emergencyContacts?.length
      ? next.emergencyContacts
      : [emptyEmergencyContact()])];
    const ec = { ...emergencyContacts[0] };
    if (ecName) ec.name = ecName;
    if (ecRelation) ec.relation = ecRelation;
    if (ecPhone) ec.phone = ecPhone;
    if (ecAddress) ec.address = ecAddress;
    emergencyContacts[0] = ec;
    next.emergencyContacts = emergencyContacts;
  }

  if (data.patient_type) {
    next.patientType = String(data.patient_type).toUpperCase();
  }

  const encounter = { ...next.encounter };
  if (data.assigned_doctor_id) encounter.admittingDoctorId = data.assigned_doctor_id;
  if (data.bed_id) encounter.bedId = data.bed_id;
  if (data.new_bed_status) encounter.newBedStatus = data.new_bed_status;
  if (data.facility_id) next.facility = data.facility_id;

  if (
    data.payer_type === "insurance"
    || data.insurance_provider
    || data.insurance_policy_no
  ) {
    encounter.financialClass = "INSURANCE";
  }
  if (data.insurance_provider) encounter.payer = data.insurance_provider;
  if (data.insurance_policy_no) encounter.insurancePolicyNo = data.insurance_policy_no;
  if (data.tpa_name) {
    encounter.financialClass = "TPA";
    encounter.payer = data.tpa_name;
  }
  if (data.government_scheme) {
    encounter.financialClass = "GOVT_SCHEME";
    encounter.payer = data.government_scheme;
  }
  if (data.corporate_name) {
    encounter.financialClass = "CORP";
    encounter.payer = data.corporate_name;
  }
  if (data.corporate_reference) encounter.insurancePolicyNo = data.corporate_reference;

  next.encounter = encounter;

  if (data.abha_id || data.abhaAddress) next.abhaAddress = data.abha_id || data.abhaAddress;
  if (data.abha_no || data.abhaNumber) next.abhaNumber = data.abha_no || data.abhaNumber;

  return next;
}

/**
 * Maps document_json onto a fresh patient object (for previews / QR list match).
 */
export function mapDocumentJsonToPatient(data) {
  return applyDocumentAutofillToPatient(
    JSON.parse(JSON.stringify(EMPTY_PATIENT)),
    data
  );
}

/**
 * Maps a patient row from get_patient_list_with_aabha_info/ onto the form.
 */
export function mapQrPatientListMatch(match) {
  if (!match) return {};

  const mapped = mapDocumentJsonToPatient({
    name: match.name || `${match.first_name || ""} ${match.last_name || ""}`.trim(),
    gender: match.gender,
    dob: match.dob,
    dateOfBirth: match.dob,
    phoneNumber: match.phone_no,
    locality: match.locality,
    pincode: match.pincode,
    state: match.state,
    abha_id: match.abha_id,
    abha_no: match.abha_no,
  });

  if (match.photo) mapped.photoUrl = match.photo;
  if (match.patient_id) {
    mapped.uhid = match.patient_id;
    mapped.patient_id = match.patient_id;
  }

  return mapped;
}
